import { MenuItem, TextField } from "@mui/material";
import { DROPDOWN_SLOT_PROPS } from "utils/dropdownSlotProps";
import { sortByText } from "utils/optionSort";

/**
 * Shared dropdown for picking a Master Data entity (Client, Manager, Member,
 * User, Activity Type, …) inside Add/Edit dialogs.
 *
 * Why this exists: dropdowns must show the COMPLETE set of options, never a
 * paginated first page (see HRMS dropdown-pagination ticket). This component
 * renders every option in `options` as a `MenuItem` — it never truncates — so a
 * dropdown is correct by default as long as it is fed a full list. The list
 * itself comes from the non-paginated `/all` service functions (Option A);
 * this component deliberately owns rendering only, not fetching, because most
 * dialogs also filter the list (e.g. members/managers by chosen client) before
 * display.
 *
 * It is a thin wrapper over MUI `<TextField select>` and forwards the native
 * change event, so it is a drop-in replacement for the hand-rolled selects the
 * dialogs used previously (same label/value/onChange/disabled semantics, same
 * accessible roles).
 *
 * Options are listed alphabetically, ordered by `getOptionLabel` rather than by any one
 * field, so each dropdown sorts on the text it actually shows — several callers label
 * their options with `fullName`, or with a name and email together. Ordering here rather
 * than in each dialog is what keeps the master data dropdowns from drifting apart again;
 * no caller depends on the incoming order, since the placeholder holds the first slot and
 * every dialog drives its selection from its own form state. @since NEXT_VERSION
 *
 * @param {Array} options            full list of entities to choose from
 * @param {Function} getOptionValue  entity -> option value (default: entity.id)
 * @param {Function} getOptionLabel  entity -> display text (default: entity.name)
 * @param {string} placeholder       text for the empty/first item
 * @param {boolean} placeholderItalic render the placeholder in <em> (default true)
 */
const MasterDataSelect = ({
  options = [],
  getOptionValue = (option) => option.id,
  getOptionLabel = (option) => option.name,
  placeholder = "Select an option",
  placeholderItalic = true,
  value,
  onChange,
  slotProps = DROPDOWN_SLOT_PROPS,
  ...rest
}) => (
  <TextField
    select
    fullWidth
    variant="outlined"
    value={value ?? ""}
    onChange={onChange}
    slotProps={slotProps}
    {...rest}
  >
    <MenuItem value="">
      {placeholderItalic ? <em>{placeholder}</em> : placeholder}
    </MenuItem>
    {sortByText(options, getOptionLabel).map((option) => {
      const optionValue = getOptionValue(option);
      return (
        <MenuItem key={optionValue} value={optionValue}>
          {getOptionLabel(option)}
        </MenuItem>
      );
    })}
  </TextField>
);

export default MasterDataSelect;
